// Rug Room Shots: room letters and room-type rules on the page (build spec 2026-10-21, sections 3.1, 3.2 and 3.9).
// The backend (GET /config roomLetters, GET /room-rules) and the worker are the source of truth; these helpers
// only word things and warn before a manual pick. They follow the same vectors as the worker and the backend:
//   allowedLetters('L>E', [E, D]) = {E};  allowedLetters('!B', [B, E, K]) = {E, K};  allowedLetters('', X) = X
//   letterAcceptable('L>E', 'E') = true;  letterAcceptable('!B', 'B') = false;  letterAcceptable('H', 'K') = false
// A letters spec is NULL / '' (any room), one negated set '!X..' (any letter except those), or 1-4 tiers of
// 1-6 letters separated by '>' (the first tier that has a room wins).

// Fallback names (GET /config roomLetters is the source of truth); any other letter shows as itself.
export const ROOM_LETTERS = [
  { code: 'L', name: 'Living room (accent chair)' },
  { code: 'E', name: 'Entry' },
  { code: 'D', name: 'Dining' },
  { code: 'K', name: 'Kitchen' },
  { code: 'H', name: 'Hallway' },
  { code: 'B', name: 'Bedroom' },
];

export const LETTERS_RE = /^(![A-Z]{1,6}|[A-Z]{1,6}(>[A-Z]{1,6}){0,3})$/;

export const RULE_LABELS = {
  round_small: 'Small round',
  round_large: 'Large round',
  round_unknown: 'Round of unknown size',
  shag_accent: 'Shag accent',
  accent_other: 'Accent',
  shag_runner: 'Shag runner',
};

// The 7th character of a room code (MC26ROD01 -> D).
export const roomLetterOf = (roomCode) => {
  const s = String(roomCode || '');
  return s.length >= 7 ? s[6].toUpperCase() : null;
};

const nameList = (letters) => (Array.isArray(letters) && letters.length ? letters : ROOM_LETTERS);

export const letterName = (letter, letters) => {
  if (!letter) return '';
  const hit = nameList(letters).find((x) => x.code === letter);
  return hit ? hit.name : letter;
};

// "L · Living room (accent chair)"
export const letterLabel = (letter, letters) => (letter ? `${letter} · ${letterName(letter, letters)}` : '');

// spec -> null (any room) | { neg: [letters] } | { tiers: [[letters], ...] }
const parseSpec = (spec) => {
  const s = String(spec || '').trim().toUpperCase();
  if (!s) return null;
  if (s.startsWith('!')) return { neg: s.slice(1).split('').filter(Boolean), tiers: [] };
  return { neg: null, tiers: s.split('>').map((t) => t.split('').filter(Boolean)).filter((t) => t.length) };
};

export const validLetters = (spec) => {
  const s = String(spec || '').trim().toUpperCase();
  return s === '' || LETTERS_RE.test(s);
};

// The letters a rug may use among the letters of the candidate rooms (spec 3.1 allowed_letters).
export const allowedLetters = (spec, available) => {
  const avail = [...new Set((available || []).map((l) => String(l || '').toUpperCase()).filter(Boolean))];
  const p = parseSpec(spec);
  if (!p) return new Set(avail);
  if (p.neg) return new Set(avail.filter((l) => !p.neg.includes(l)));
  const tier = p.tiers.find((t) => t.some((l) => avail.includes(l)));
  return new Set(tier ? tier.filter((l) => avail.includes(l)) : []);
};

// Warning flags only (spec 3.1 letter_acceptable): any tier counts.
export const letterAcceptable = (spec, letter) => {
  const p = parseSpec(spec);
  if (!p) return true;
  const l = String(letter || '').toUpperCase();
  if (p.neg) return !p.neg.includes(l);
  return p.tiers.some((t) => t.includes(l));
};

// Spec 3.9 letters phrase: "dining", "living room (accent chair), else entry", "any type except bedroom".
export const describeLetters = (spec, letters) => {
  const p = parseSpec(spec);
  if (!p) return 'any room type';
  const nm = (l) => letterName(l, letters).toLowerCase();
  if (p.neg) return `any type except ${p.neg.map(nm).join(' or ')}`;
  return p.tiers.map((t) => t.map(nm).join(' or ')).join(', else ');
};

// Several rugs' specs at once (bulk "Choose the room for each group", review fix 2026-10-21): the specs joined by '|'
// (never part of the grammar). A room is acceptable when ANY rug of the group would use that type, and '' (any room)
// when one of them takes any room. groupLettersSpec([{ letters: 'L', count: 4 }, { letters: 'D', count: 2 }]) = 'L|D'.
export const groupLettersSpec = (list) => {
  const specs = (Array.isArray(list) ? list : []).map((x) => (x && x.letters ? String(x.letters).trim().toUpperCase() : ''));
  if (!specs.length || specs.some((s) => s === '' || !LETTERS_RE.test(s))) return '';
  return [...new Set(specs)].sort().join('|');
};
const anySpecs = (spec) => String(spec || '').split('|').map((s) => s.trim()).filter(Boolean);
export const letterAcceptableAny = (spec, letter) => {
  const specs = anySpecs(spec);
  return !specs.length || specs.some((s) => letterAcceptable(s, letter));
};
export const describeLettersAny = (spec, letters) => {
  const specs = anySpecs(spec);
  return specs.length ? specs.map((s) => describeLetters(s, letters)).join('; or ') : describeLetters('', letters);
};

// Spec 3.9 rule phrases. smallMax is round_small_max (code default 6) from GET /room-rules when the page has it.
export const rulePhrase = (rule, smallMax) => {
  const n = Number(smallMax) > 0 ? Math.round(Number(smallMax)) : 6;
  switch (rule) {
    case 'round_small': return `rounds up to ${n}R`;
    case 'round_large': return `rounds ${n + 1}R and up`;
    case 'shag_accent': return 'shag accent rugs';
    case 'accent_other': return 'accent rugs that are not shag';
    case 'shag_runner': return 'shag runners';
    default: return '';
  }
};

// "rounds 7R and up use dining rooms", "accent rugs that are not shag do not use bedroom rooms" ('' when unknown).
export const ruleUseSentence = (rule, spec, smallMax, letters) => {
  const phrase = rulePhrase(rule, smallMax);
  const p = parseSpec(spec);
  if (!phrase || !p) return '';
  const nm = (l) => letterName(l, letters).toLowerCase();
  if (p.neg) return `${phrase} do not use ${p.neg.map(nm).join(' or ')} rooms`;
  return `${phrase} use ${p.tiers.map((t) => t.map(nm).join(' or ')).join(', else ')} rooms`;
};

// The text of the warning before a manual pick of a room that is not the usual type (spec 4.7).
export const notUsualRoomText = (rule, spec, smallMax, letters) => {
  const why = ruleUseSentence(rule, spec, smallMax, letters);
  return `This room is not the usual type for this rug${why ? ` (${why})` : ''}.`;
};

// The base photo of a room shows a rug of another shape (base_shape_ok = 0): today the oval-rug bases.
// Such rooms are never picked automatically; a person may still choose one.
export const baseShapeLabel = (base) => {
  const shape = base && base.shape ? String(base.shape) : '';
  return !shape || /oval/i.test(shape) ? 'oval base photo' : 'base shape mismatch';
};

export const baseShapeTip = (base) => {
  const shape = base && base.shape ? String(base.shape) : '';
  const what = !shape || /oval/i.test(shape) ? 'an oval rug' : `a ${shape.toLowerCase()} rug`;
  return `The room's base photo shows ${what}${base && base.sku ? ` (${base.sku})` : ''}, so it is never picked automatically and a result may copy that shape.`;
};

// A room (GET /rooms item): its letter and whether its base photo shows a rug of another shape.
export const roomLetterOfRoom = (r) => (r && (r.letter || roomLetterOf(r.roomCode))) || null;
export const roomBaseShapeBad = (r) => !!(r && r.base && r.base.shapeOk === false);

// Warnings before a manual room choice for a rug (item has roomLetters / roomRule): not the usual room type,
// or a base photo that shows an oval rug (build spec 4.7). Empty when the room is fine.
export const roomPickWarnings = (item, room, smallMax, letters) => {
  const out = [];
  if (!item || !room) return out;
  if (item.roomLetters && !letterAcceptable(item.roomLetters, roomLetterOfRoom(room))) {
    out.push(notUsualRoomText(item.roomRule, item.roomLetters, smallMax, letters));
  }
  if (roomBaseShapeBad(room)) out.push(baseShapeTip(room.base));
  return out;
};

// Effective rules in words (Rooms tab "Room type rules" panel). r = GET /room-rules "effective".
export const effectiveRuleLines = (r, letters) => {
  if (!r) return [];
  const n = Number(r.round_small_max) || 6;
  const lettersWords = (spec) => (String(spec || '') === '' ? 'rule off (any room type)' : describeLetters(spec, letters));
  const sizes = Array.isArray(r.shag_accent_sizes) ? r.shag_accent_sizes : [];
  const types = Array.isArray(r.shag_rug_types) ? r.shag_rug_types : [];
  const skip = Array.isArray(r.shag_rules_skip_styles) ? r.shag_rules_skip_styles : [];
  return [
    { key: 'round_small', label: `Rounds up to ${n}R`, value: lettersWords(r.round_small_letters) },
    { key: 'round_large', label: `Rounds ${n + 1}R and up`, value: lettersWords(r.round_large_letters) },
    {
      key: 'shag_accent',
      label: `Shag accent rugs${sizes.length ? ` (sizes ${sizes.map((s) => `-${s}`).join(', ')})` : ' (no sizes: rule off)'}`,
      value: sizes.length ? lettersWords(r.shag_accent_letters) : 'rule off',
    },
    { key: 'accent_other', label: 'Other accent rugs', value: lettersWords(r.accent_other_letters) },
    { key: 'shag_runner', label: 'Shag runners', value: lettersWords(r.shag_runner_letters) },
    { key: 'shag_rug_types', label: 'Shag means rug type', value: types.length ? types.join(', ') : '-' },
    { key: 'shag_rules_skip_styles', label: 'Styles the accent and runner rules skip', value: skip.length ? skip.join(', ') : 'none' },
  ];
};
